/**
 * API サービス - フライト管理用
 *
 * API Gateway への HTTP リクエストを管理
 * Cognito ID Token を自動付与
 */

import { getSession, signOut } from './auth';

const API_BASE = process.env.REACT_APP_API_ENDPOINT;

/**
 * 認証付き fetch
 */
async function authFetch(path, options = {}) {
  let session;
  try {
    session = await getSession();
  } catch (err) {
    // セッション切れ → ログアウト
    signOut();
    window.location.href = '/login';
    throw new Error('Session expired');
  }

  const url = `${API_BASE}${path}`;
  const headers = {
    'Content-Type': 'application/json',
    Authorization: session.idToken,
    ...options.headers,
  };

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    signOut();
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || `API Error: ${response.status}`);
  }

  return data;
}

// ============================================================
// DIPS API (ドローン情報基盤システム連携)
// ============================================================

const DIPS_TOKEN_KEY = 'dips_token';
const DIPS_FLIGHTPLAN_TOKEN_KEY = 'dips_flightplan_token';

export const dipsApi = {
  /**
   * DIPSトークンをローカルストレージから取得
   */
  getStoredToken: () => {
    const stored = localStorage.getItem(DIPS_TOKEN_KEY);
    if (!stored) return null;
    try {
      const data = JSON.parse(stored);
      // 30秒前に期限切れとみなす（トークン有効期限が短い場合も考慮）
      if (data.expiresAt && Date.now() > data.expiresAt - 30 * 1000) {
        localStorage.removeItem(DIPS_TOKEN_KEY);
        return null;
      }
      return data;
    } catch {
      return null;
    }
  },

  /**
   * DIPSトークンを保存
   */
  storeToken: (tokenData) => {
    const expiresAt = Date.now() + (tokenData.expiresIn || 300) * 1000;
    localStorage.setItem(DIPS_TOKEN_KEY, JSON.stringify({
      accessToken: tokenData.accessToken,
      refreshToken: tokenData.refreshToken,
      expiresAt,
    }));
  },

  /**
   * DIPSトークンをクリア
   */
  clearToken: () => {
    localStorage.removeItem(DIPS_TOKEN_KEY);
  },

  // ============================================================
  // 飛行計画用トークン管理（別のclient_idが必要）
  // ============================================================

  /**
   * 飛行計画用トークンをローカルストレージから取得
   */
  getStoredFlightPlanToken: () => {
    const stored = localStorage.getItem(DIPS_FLIGHTPLAN_TOKEN_KEY);
    if (!stored) return null;
    try {
      const data = JSON.parse(stored);
      // 30秒前に期限切れとみなす（トークン有効期限が短い場合も考慮）
      if (data.expiresAt && Date.now() > data.expiresAt - 30 * 1000) {
        localStorage.removeItem(DIPS_FLIGHTPLAN_TOKEN_KEY);
        return null;
      }
      return data;
    } catch {
      return null;
    }
  },

  /**
   * 飛行計画用トークンを保存
   */
  storeFlightPlanToken: (tokenData) => {
    const expiresAt = Date.now() + (tokenData.expiresIn || 300) * 1000;
    localStorage.setItem(DIPS_FLIGHTPLAN_TOKEN_KEY, JSON.stringify({
      accessToken: tokenData.accessToken,
      refreshToken: tokenData.refreshToken,
      expiresAt,
    }));
  },

  /**
   * 飛行計画用トークンをクリア
   */
  clearFlightPlanToken: () => {
    localStorage.removeItem(DIPS_FLIGHTPLAN_TOKEN_KEY);
  },

  /**
   * 飛行計画認証済みかどうかチェック
   */
  isFlightPlanAuthenticated: () => {
    const token = dipsApi.getStoredFlightPlanToken();
    return token !== null;
  },

  /**
   * DIPS認証URLを取得
   */
  getAuthUrl: (apiType = 'aircraft') =>
    authFetch(`/dips/auth/url?apiType=${apiType}`),

  /**
   * 認可コードをトークンに交換
   * 注: このエンドポイントはCognito認証不要（別ドメインのポップアップから呼び出されるため）
   */
  exchangeToken: async (code, apiType = 'aircraft') => {
    const url = `${API_BASE}/dips/token`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, apiType }),
    });
    const text = await response.text();
    let result;
    try {
      result = JSON.parse(text);
    } catch (e) {
      console.error('[DIPS] Failed to parse token response:', text);
      throw new Error(`Token exchange failed: ${response.status} - ${text || 'Empty response'}`);
    }
    if (!response.ok) {
      throw new Error(result.message || `API Error: ${response.status}`);
    }
    if (result.accessToken) {
      // API種別に応じて適切なストレージに保存
      if (apiType === 'flightPlan') {
        dipsApi.storeFlightPlanToken(result);
      } else {
        dipsApi.storeToken(result);
      }
    }
    return result;
  },

  /**
   * DIPS機体一覧を取得
   */
  getAircrafts: async () => {
    const token = dipsApi.getStoredToken();
    if (!token) {
      throw new Error('DIPS認証が必要です');
    }
    return authFetch('/dips/aircrafts', {
      headers: {
        'X-DIPS-Token': token.accessToken,
      },
    });
  },

  /**
   * DIPS飛行計画を検索
   * @param {Object} searchParams - 検索パラメータ
   * @param {string} searchParams.registration_code - 機体登録記号（オプション）
   * @param {string} searchParams.start_date - 検索開始日（オプション）
   * @param {string} searchParams.end_date - 検索終了日（オプション）
   */
  getFlightPlans: async (searchParams = {}) => {
    const token = dipsApi.getStoredFlightPlanToken();
    if (!token) {
      throw new Error('飛行計画認証が必要です');
    }
    return authFetch('/dips/flightplans', {
      method: 'POST',
      headers: {
        'X-DIPS-Token': token.accessToken,
      },
      body: JSON.stringify(searchParams),
    });
  },

  /**
   * DIPS飛行計画を通報（登録）
   * @param {Object} reportData - 飛行計画データ
   */
  reportFlightPlan: async (reportData) => {
    const token = dipsApi.getStoredFlightPlanToken();
    if (!token) {
      throw new Error('飛行計画認証が必要です');
    }
    return authFetch('/dips/flightplans/report', {
      method: 'POST',
      headers: {
        'X-DIPS-Token': token.accessToken,
      },
      body: JSON.stringify(reportData),
    });
  },

  /**
   * DIPS接続テスト
   */
  test: () => authFetch('/dips/test'),

  /**
   * 認証済みかどうかチェック
   */
  isAuthenticated: () => {
    const token = dipsApi.getStoredToken();
    return token !== null;
  },
};
